// src/pages/RaiseABuilder.tsx
// Route: /raise-a-builder — the second sales page for parents, written from
// the academy's own marketing (curriculum/MARKETING: the campaign playbook,
// the documentary script and the handbooks), kept short.
//
// Order follows the CREATOROS long sales page blueprint (Brunson's sales
// letter): who it's for → promise → the problem → the story → the new way →
// how it works (the parent's journey) → what they'll have done by 18 →
// what's included → price → objections → one call to action → P.S.
// No invented testimonials or numbers. Prices, the free first week, the
// 14-day money-back guarantee and the promise are the live ones
// (components/OfferPromise.tsx, backend services/pricing.js).

import { Link } from "react-router-dom";
import {
  ArrowRight,
  Award,
  BadgeCheck,
  Check,
  Hammer,
  LayoutDashboard,
  Lightbulb,
  Mail,
  Package,
  PlayCircle,
  Rocket,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Seo from "../components/Seo";
import OfferPromise, { PRICE_NOTE } from "../components/OfferPromise";

const SIGN_UP = "/register";
const WHATSAPP = "https://wa.me/2347052499758";
const naira = (n: number) => `₦${n.toLocaleString("en-NG")}`;

function Cta({ label = "Start my child's journey", note = true, dark = false }: { label?: string; note?: boolean; dark?: boolean }) {
  return (
    <div className="flex flex-col items-start gap-2.5">
      <Link
        to={SIGN_UP}
        className="inline-flex items-center gap-2 rounded-2xl bg-gold-400 px-7 py-4 text-base font-extrabold text-ink shadow-[0_14px_36px_rgba(201,162,39,0.35)] transition hover:-translate-y-0.5 hover:bg-gold-300"
      >
        {label} <ArrowRight className="h-5 w-5" />
      </Link>
      {note && <p className={`text-sm ${dark ? "text-white/65" : "text-ink-mute"}`}>Online, anywhere in Nigeria · First week free · From {naira(18000)} a month</p>}
    </div>
  );
}

const Kicker = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[13px] font-extrabold uppercase tracking-[0.16em] text-gold-500">{children}</p>
);
const H2 = ({ children, light = false }: { children: React.ReactNode; light?: boolean }) => (
  <h2
    className={`mt-2 text-[1.8rem] font-extrabold leading-[1.15] tracking-[-0.02em] sm:text-[2.3rem] ${light ? "text-white" : "text-ink"}`}
    style={{ textWrap: "balance" } as React.CSSProperties}
  >
    {children}
  </h2>
);

const PROMISE: { icon: LucideIcon; title: string; steps: string[] }[] = [
  { icon: Lightbulb, title: "Find the idea", steps: ["6: what is a problem?", "11: talk to the people who have it", "16: size the market"] },
  { icon: Hammer, title: "Build it", steps: ["7: a Scratch game", "10: a live website", "15: a full-stack app with AI"] },
  { icon: Wallet, title: "Raise the money", steps: ["7: wants vs needs", "12: a startup budget", "17: a cap table and an investor pitch"] },
  { icon: Users, title: "Lead the team", steps: ["Every week: explain their work", "Every term: present it", "18: lead a capstone team"] },
];

const JOURNEY: { icon: LucideIcon; title: string; text: string; img?: string; alt?: string }[] = [
  { icon: UserPlus, title: "Create your family account", text: "Five minutes. Add your child and choose their age group and payment plan." },
  { icon: Package, title: "Their kit arrives", text: "A hardware kit is posted to your home each year: circuits and Makey Makey, then Arduino and sensors." },
  { icon: PlayCircle, title: "Lessons, one step at a time", text: "Get ready, watch a short video, do the activity, hand in. About 30–40 minutes, any time in the week.", img: "/images/sales/lesson-ready.jpg", alt: "A lesson's first step: what you'll learn and what to have ready" },
  { icon: BadgeCheck, title: "A tutor reviews every piece", text: "What went well, one thing to improve, the next step. Usually within 48 hours.", img: "/images/sales/feedback.jpg", alt: "Tutor feedback on a child's work" },
  { icon: LayoutDashboard, title: "You see everything", text: "Your dashboard and a weekly email show progress, feedback and anything behind.", img: "/images/sales/parent-dashboard.jpg", alt: "The parent dashboard" },
  { icon: Award, title: "A showcase every term", text: "Your child pitches what they built. A panel scores it and they earn a certificate." },
];

const GRADUATE = [
  "Built and deployed a full-stack web application",
  "Built a mobile app and an Internet of Things device",
  "Put real AI into a product people use",
  "Pitched to a panel, again and again",
  "Understands compound interest, equity and startup finance",
  "Speaks with confidence in any room",
];

const INCLUDED = [
  "675 short video lessons, ages 6 to 18",
  "A tutor's review of every piece of work",
  "A hardware kit posted every year",
  "A showcase and certificate every term",
  "Weekly progress emails for you",
  "Child mode for 6–9; their own sign-in from 9",
];

const PRICES = [
  { ages: "Ages 6–9", name: "Foundation Builders", monthly: 18000 },
  { ages: "Ages 9–12", name: "Explorers", monthly: 22000 },
  { ages: "Ages 13–18", name: "Innovators & Founders", monthly: 28000 },
];

const FAQ = [
  { q: "Isn't my child too young?", a: "We start at 6 with Scratch, which is closer to Lego than programming. No child is too young to learn that their ideas can become things." },
  { q: "Isn't this just more screen time?", a: "A child watching YouTube is a consumer. A child building a game, wiring a circuit or publishing a website is a creator. Much of the work happens away from the screen." },
  { q: "My child isn't into technology.", a: "They're into games, music, drawing or building with their hands. Each is a way in. We start where their curiosity is." },
  { q: "Will they really raise money?", a: "They learn exactly how founders raise money and pitch to a panel. We don't ask anyone under 18 to apply for investment, and we don't promise funding." },
  { q: "Are there live classes?", a: "No. Short video lessons fit around your family's week, and a tutor reviews everything. Hybrid and on-site are opening area by area." },
  { q: "Is it worth the cost?", a: "A lesson teacher for one subject costs ₦15,000–30,000 a month. This builds a way of thinking that serves your child in any career. Pay quarterly and save 10%, yearly and save 20%, and siblings get a family discount." },
  { q: "Can we try it first?", a: "Yes. Your child's first week is free: create your family account, add your child and open Week 1. Every lesson, with tutor feedback, for 7 days. No payment, no card." },
  { q: "What if it isn't right for us?", a: "Ask for a full refund within 14 days of your first payment, straight from your Payments page. No hard feelings." },
  { q: "Is it safe?", a: "Work stays private to your family and the tutor. Public projects show a first name only, and children never message each other or strangers." },
];

export default function RaiseABuilder() {
  return (
    <div className="min-h-screen bg-paper font-sans text-ink antialiased">
      <Seo
        title="Raise a builder"
        description="Your child was born into a world that will be run by builders. Future Smart Lab makes sure they're one of them: by 18 they can find an idea, build it, raise money for it and lead the team."
        path="/raise-a-builder"
      />

      <header className="sticky top-0 z-30 border-b border-[#e5e8ef] bg-paper/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <span className="flex items-center gap-2.5">
            <img src="/images/logo.png" alt="" className="h-9 w-9 rounded-xl" />
            <span className="text-[15px] font-extrabold tracking-[-0.02em]">Future Smart Lab</span>
          </span>
          <Link to={SIGN_UP} className="rounded-xl bg-brand-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-brand-700">
            Get started
          </Link>
        </div>
      </header>

      {/* Who it's for · the promise */}
      <section className="relative overflow-hidden bg-brand-600 text-white">
        <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-gold-400/20 blur-3xl" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 sm:px-6 sm:py-20 lg:grid-cols-2">
          <div>
            <p className="text-[13px] font-bold uppercase tracking-[0.14em] text-gold-300">For parents of children aged 6 to 18</p>
            <h1 className="mt-4 text-[2.3rem] font-extrabold leading-[1.06] tracking-[-0.03em] sm:text-[3.4rem]" style={{ textWrap: "balance" } as React.CSSProperties}>
              By 18, your child can build a tech business.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-8 text-white/80">
              Find the idea. Build it. Raise the money. Lead the team. Future Smart Lab forms children aged 6 to 18 into
              builders, one short lesson at a time.
            </p>
            <div className="mt-8">
              <Cta dark />
            </div>
          </div>
          <img src="/images/raise/robot-team.jpg" alt="Two girls wiring up a robot together" className="w-full rounded-[28px] object-cover shadow-[0_30px_80px_rgba(0,0,0,0.35)]" />
        </div>
      </section>

      <main>
        {/* The problem */}
        <section className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 sm:py-20">
          <Kicker>The real question</Kicker>
          <H2>There are two groups of people in every economy.</H2>
          <p className="mt-5 text-lg leading-8 text-ink-soft">
            Those who build the technology, and those who are managed by it. School teaches your child to pass exams.
            Most coding classes stop at the code. Neither answers the question that matters:{" "}
            <b className="text-ink">who is your child becoming?</b>
          </p>
        </section>

        {/* The story */}
        <section className="bg-white">
          <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-[0.9fr_1.1fr]">
            <img src="/images/landing/builder.jpg" alt="A teenager at work on a laptop, headphones on" className="w-full rounded-[28px] object-cover lg:max-h-[560px]" loading="lazy" />
            <div>
              <Kicker>Why we built Future Smart Lab</Kicker>
              <H2>The next Paystack is being raised right now.</H2>
              <p className="mt-5 text-lg leading-8 text-ink-soft">
                Paystack, Flutterwave, Andela, PiggyVest: built by Nigerians who believed they could build at world scale.
                The next wave of those founders is between 6 and 18 today. We exist to find them, form them and equip them,
                before the world tells them what they can't do.
              </p>
              <p className="mt-4 text-lg font-bold leading-8 text-ink">Possibly in your home.</p>
            </div>
          </div>
        </section>

        {/* The new way: formation, the four-part promise */}
        <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <Kicker>Formation, not tutoring</Kicker>
            <H2>A child who is tutored knows more. A child who is formed becomes more.</H2>
            <p className="mt-4 text-lg leading-8 text-ink-soft">Every week builds the four things a founder needs, step by step from age 6.</p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PROMISE.map(({ icon: Icon, title, steps }) => (
              <div key={title} className="rounded-[24px] bg-white p-6 ring-1 ring-[#e5e8ef]">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-600 text-white">
                  <Icon className="h-6 w-6" />
                </span>
                <p className="mt-4 text-lg font-extrabold text-ink">{title}</p>
                <ul className="mt-3 space-y-2">
                  {steps.map((s) => {
                    const [age, rest] = s.includes(": ") ? s.split(": ") : ["", s];
                    return (
                      <li key={s} className="text-[15px] leading-6 text-ink-soft">
                        {age && <b className="text-gold-500">{/^\d/.test(age) ? `Age ${age}` : age}: </b>}
                        {rest}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* How it works: the parent's journey */}
        <section className="bg-white">
          <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20">
            <div className="text-center">
              <Kicker>How it works</Kicker>
              <H2>Your family's journey, step by step</H2>
            </div>
            <ol className="mt-12 space-y-10">
              {JOURNEY.map(({ icon: Icon, title, text, img, alt }, i) => (
                <li key={title} className={`grid items-center gap-6 ${img ? "md:grid-cols-2" : ""}`}>
                  <div className={`flex gap-4 ${img && i % 2 ? "md:order-2" : ""}`}>
                    <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gold-400 text-lg font-extrabold text-ink">
                      {i + 1}
                    </span>
                    <div>
                      <p className="flex items-center gap-2 text-xl font-extrabold text-ink">
                        <Icon className="h-5 w-5 text-brand-600" /> {title}
                      </p>
                      <p className="mt-1.5 text-[16px] leading-7 text-ink-mute">{text}</p>
                    </div>
                  </div>
                  {img && (
                    <figure className="overflow-hidden rounded-[22px] shadow-[0_20px_50px_rgba(27,45,94,0.14)] ring-1 ring-[#e5e8ef]">
                      <img src={img} alt={alt} loading="lazy" className="block max-h-[380px] w-full object-cover object-top" />
                    </figure>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* What they'll have done by 18 */}
        <section className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-2">
          <div>
            <Kicker>The standard at 18</Kicker>
            <H2>What your child will have done</H2>
            <ul className="mt-6 space-y-3">
              {GRADUATE.map((g) => (
                <li key={g} className="flex gap-3 text-[17px] leading-7 text-ink-soft">
                  <Check className="mt-1 h-5 w-5 flex-shrink-0 text-[#2a7a56]" /> {g}
                </li>
              ))}
            </ul>
            <p className="mt-6 flex items-center gap-2 text-[16px] font-bold text-ink">
              <Rocket className="h-5 w-5 text-gold-500" /> Not a certificate in coding. The start of a company.
            </p>
          </div>
          <img src="/images/landing/hero.jpg" alt="A smiling learner holding her laptop" className="w-full rounded-[28px] object-cover lg:max-h-[560px]" loading="lazy" />
        </section>

        {/* What's included · price */}
        <section className="bg-brand-600 text-white">
          <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20">
            <div className="text-center">
              <Kicker>Everything included</Kicker>
              <H2 light>One monthly fee. Everything your child needs.</H2>
            </div>
            <ul className="mx-auto mt-8 grid max-w-3xl gap-x-8 gap-y-3 sm:grid-cols-2">
              {INCLUDED.map((x) => (
                <li key={x} className="flex gap-3 text-[16px] text-white/85">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-gold-300" /> {x}
                </li>
              ))}
            </ul>
            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {PRICES.map((p) => (
                <div key={p.name} className="rounded-[22px] bg-white/10 p-5 text-center ring-1 ring-white/15">
                  <p className="text-[13px] font-bold text-gold-300">{p.ages}</p>
                  <p className="text-sm text-white/75">{p.name}</p>
                  <p className="mt-3 text-[2rem] font-extrabold tabular-nums">{naira(p.monthly)}</p>
                  <p className="text-sm text-white/65">a month, per child</p>
                </div>
              ))}
            </div>
            <p className="mx-auto mt-5 max-w-2xl text-center text-[15px] text-white/75">{PRICE_NOTE}</p>
            <div className="mt-10">
              <OfferPromise dark />
            </div>
            <div className="mt-8 flex justify-center">
              <Cta label="Start my child's free week" note={false} />
            </div>
          </div>
        </section>

        {/* Objections */}
        <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="text-center">
            <Kicker>Parents ask</Kicker>
            <H2>Quick answers</H2>
          </div>
          <div className="mt-8 divide-y divide-[#e5e8ef] overflow-hidden rounded-[24px] bg-white ring-1 ring-[#e5e8ef]">
            {FAQ.map((f) => (
              <details key={f.q} className="group p-5">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-[16px] font-bold text-ink">
                  {f.q}
                  <span className="text-2xl leading-none text-gold-500 transition group-open:rotate-45">+</span>
                </summary>
                <p className="mt-2.5 text-[15px] leading-7 text-ink-mute">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Final call · P.S. */}
        <section className="bg-white">
          <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-2">
            <img src="/images/raise/young-builder.jpg" alt="A young child with a robot they're building" className="w-full rounded-[28px] object-cover" loading="lazy" />
            <div>
              <H2>Your child can absolutely do this.</H2>
              <p className="mt-4 text-lg leading-8 text-ink-soft">
                All we ask of them is curiosity. All we ask of you is the decision to give that curiosity somewhere to grow.
              </p>
              <div className="mt-7">
                <Cta label="Create my family account" />
              </div>
              <p className="mt-8 rounded-[20px] bg-paper p-5 text-[15px] leading-7 text-ink-soft">
                <b className="text-gold-500">P.S.</b> Time is the one thing you can't give them back. Every term they start sooner is
                another term of building, pitching and growing. Questions first?{" "}
                <a href={WHATSAPP} target="_blank" rel="noreferrer" className="font-bold text-brand-600 hover:underline">
                  Ask us on WhatsApp
                </a>
                .
              </p>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-ink px-4 py-7 text-center text-[13px] text-white/50">
        <p className="inline-flex items-center gap-2">
          <Mail className="h-4 w-4" /> hello@futuresmartlab.com · Future Smart Lab, Abuja
        </p>
        <p className="mt-2">
          <Link to="/" className="hover:text-white">futuresmartlab.com</Link>
        </p>
      </footer>
    </div>
  );
}
